
import { defineComponent, PropType, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import Nodata from '@/components/page/nodata.vue'
import HeaderSettingsPanel from './HeaderSettingsPanel.vue'
import DragonSignalBacktest from './DragonSignalBacktest.vue'
import DragonHunterBacktest from './DragonHunterBacktest.vue'
import { getTableColumns, TableColumn, getCourseName, getHotCasesColumns, getCourseConfig } from './courseConfig'
import { formatNumber, formatPercent, formatAmount, maskStockName, maskStockCode, isMaskEnabled, isEtfStock, formatPrice } from './tableColumns'
import { getChipStateColor, getChipShapeColor, getInPoolTimeColor, formatInPoolTime } from './chipMasterUtils'
import { formatMengzhuDate, getChangeColor } from './mengzhuTrendUtils'
import {
  generateStorageKey,
  saveColumnOrder,
  getColumnOrder,
  clearColumnOrder,
  validateColumnConfig,
  filterDraggableColumns,
  applyColumnOrder,
  generateColumnConfigVersion,
  ListType,
  SignalType
} from './columnOrderService'

interface PoolData {
  symbol: string
  market: string
  name: string
  [key: string]: any
}

export default defineComponent({
  name: 'PoolTable',
  components: { Nodata, HeaderSettingsPanel, DragonSignalBacktest, DragonHunterBacktest },
  props: {
    dataSource: {
      type: Array as PropType<PoolData[]>,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: true
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'light'
    },
    courseNo: {
      type: String,
      default: 'e4408ecedc054856946546a185e8c047'
    },
    productData: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    isClick: {
      type: Number,
      default: 2  // 1: 弹窗模式(只有双击) 2: 内嵌模式(单击+双击)
    },
    defaultSortField: {
      type: String,
      default: 'pxChangRate'
    },
    defaultSortOrder: {
      type: String,
      default: 'descend'
    },
    // 当前排序字段（受控模式）
    sortFieldProp: {
      type: String,
      default: ''
    },
    // 当前排序顺序（受控模式）
    sortOrderProp: {
      type: String,
      default: ''
    },
    // 盟主趋势顶级 Tab
    mengzhuMainTab: {
      type: String,
      default: 'signalPool'
    },
    // 盟主趋势：最后一个有效交易日（用于判断最高涨幅和累计涨幅的显示逻辑）
    mengzhuLastTradingDay: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号池更新时间
    mengzhuUpdateTime: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号类型（latest 或 history）
    mengzhuSignalType: {
      type: String,
      default: 'latest'
    },
    // 龙头信号
    isDragonSignal: {
      type: Boolean,
      default: false
    },
    dragonSignalStrategyCode: {
      type: String,
      default: 'dragon_signal_sprint'
    },
    // 龙头猎手
    isDragonHunter: {
      type: Boolean,
      default: false
    },
    dragonHunterMainTab: {
      type: String,
      default: 'newChose'
    },
    dragonHunterSubTab: {
      type: String,
      default: 'dragonStar'
    }
  },
  emits: ['change', 'row-click', 'row-dblclick', 'load-more', 'visible-range-change', 'open-ai-interp'],
  setup(props, { emit, expose }) {
    const route = useRoute()
    const tableWrapperRef = ref<HTMLElement | null>(null)
    const tableBodyRef = ref<HTMLElement | null>(null)
    const tableHeaderRef = ref<HTMLElement | null>(null)
    // 设置图标 hover 状态
    const settingsIconHover = ref(false)
    const settingsIconSrc = computed(() => {
      if (props.theme === 'dark') {
        return settingsIconHover.value ? require('@/assets/heiseactive.png') : require('@/assets/heise.png')
      }
      return settingsIconHover.value ? require('@/assets/baiseactive.png') : require('@/assets/baise.png')
    })
    // 初始化时使用默认排序，如果传入了受控 props 则优先使用
    const sortField = ref<string>(props.sortFieldProp || props.defaultSortField)
    const sortOrder = ref<string>(props.sortOrderProp || props.defaultSortOrder)

    // 监听受控 props 变化
    watch(() => props.sortFieldProp, (newVal) => {
      // 如果 props 传了值，同步到内部 ref
      if (newVal !== undefined) {
        sortField.value = newVal
      }
    })
    watch(() => props.sortOrderProp, (newVal) => {
      // 如果 props 传了值，同步到内部 ref
      if (newVal !== undefined) {
        sortOrder.value = newVal === 'asc' ? 'ascend' : (newVal === 'desc' ? 'descend' : newVal)
      }
    })

    // 监听父组件的重置方法调用，直接覆盖内部状态，确保不触发默认恢复
    const resetSort = (newField?: string, newOrder?: string) => {
      if (newField !== undefined) {
        sortField.value = newField
      } else {
        sortField.value = props.defaultSortField
      }

      if (newOrder !== undefined) {
        sortOrder.value = newOrder === 'asc' ? 'ascend' : (newOrder === 'desc' ? 'descend' : newOrder)
      } else {
        sortOrder.value = props.defaultSortOrder
      }
    }

    // ==================== 可视区域订阅优化（IntersectionObserver） ====================
    // 用 IntersectionObserver 精确检测哪些行在可视区域内
    let intersectionObserver: IntersectionObserver | null = null
    const visibleSymbols = new Set<string>() // 当前可视区域内的 symbol 集合
    let visibleChangeTimer: ReturnType<typeof setTimeout> | null = null

    // 防抖发射可视区域变化
    const debouncedEmitVisibleRange = () => {
      if (visibleChangeTimer) {
        clearTimeout(visibleChangeTimer)
      }
      visibleChangeTimer = setTimeout(() => {
        visibleChangeTimer = null
        // 从 dataSource 中筛选出可视区域内的数据
        const visibleData = props.dataSource.filter((item: any) => visibleSymbols.has(item.symbol))
        emit('visible-range-change', visibleData)
      }, 500)
    }

    // 创建 IntersectionObserver 实例
    const createObserver = () => {
      if (intersectionObserver) {
        intersectionObserver.disconnect()
      }
      const root = tableBodyRef.value
      if (!root) return

      intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const symbol = (entry.target as HTMLElement).dataset.symbol
          if (!symbol) return
          if (entry.isIntersecting) {
            visibleSymbols.add(symbol)
          } else {
            visibleSymbols.delete(symbol)
          }
        })
        debouncedEmitVisibleRange()
      }, {
        root: root,
        rootMargin: '250px 0px' // 上下各扩展100px作为缓冲区
      })
    }

    // 观察所有数据行
    const observeRows = () => {
      if (!intersectionObserver) return
      const container = tableBodyRef.value
      if (!container) return
      // 查找所有带 data-symbol 的行
      const rows = container.querySelectorAll('tr[data-symbol]')
      rows.forEach((row) => {
        intersectionObserver!.observe(row)
      })
    }

    // 重新观察（数据变化后调用）
    const refreshObserver = () => {
      visibleSymbols.clear()
      // 清除防抖定时器，避免 clear 后立即发出空数组
      if (visibleChangeTimer) {
        clearTimeout(visibleChangeTimer)
        visibleChangeTimer = null
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect()
      }
      // 等 DOM 更新后再观察，观察完后再等 IntersectionObserver 回调稳定
      setTimeout(() => {
        observeRows()
        // IntersectionObserver 回调是异步的，等它填充完 visibleSymbols 后再 emit
        setTimeout(() => {
          debouncedEmitVisibleRange()
        }, 200)
      }, 50)
    }

    // 暴露给父组件的方法
    const emitVisibleRange = () => {
      const visibleData = props.dataSource.filter((item: any) => visibleSymbols.has(item.symbol))
      emit('visible-range-change', visibleData)
    }

    // 判断是否启用敏感信息打码
    const shouldMask = computed(() => isMaskEnabled(route.query.mcnMode as string))

    // 表格容器样式：只有 os=px 时才设置固定高度
    const tableContainerStyle = computed(() => {
      if (route.query.os === 'xp') {
        return { height: 'calc(100vh - 192px)' }
      }
      return {}
    })

    // 获取显示的股票名称（根据打码模式）
    const getDisplayName = (name: string) => {
      return shouldMask.value ? maskStockName(name) : name
    }

    // 获取显示的股票代码（根据打码模式）
    const getDisplayCode = (code: string) => {
      return shouldMask.value ? maskStockCode(code) : code
    }

    // 暴露方法给父组件
    expose({ resetSort, emitVisibleRange })

    // 判断是否为盟主趋势（需要在 columns 之前定义，因为 columns 依赖它）
    const isMengzhuTrend = computed(() => props.courseNo === '399a9139a99a4300a9bf004b9949eb9f')

    // 判断是否为箱体擒龙
    const isBoxDragon = computed(() => props.courseNo === '33130259066e4650bd0b7cf6b23d9488')

    // 判断是否为龙头信号
    const isDragonSignal = computed(() => props.courseNo === 'b20f2d6e654948ae8c99bf650cbbb0e2')

    // 判断是否为龙头猎手
    const isDragonHunter = computed(() => props.courseNo === '03fc903a5ac84ff6982e4b83a1f35c61')

    // 格式化个股热度（龙头信号特有）
    const formatStockHot = (val: number | null | undefined) => {
      if (val === null || val === undefined) return '--'
      if (val >= 10000) return (val / 10000).toFixed(1) + '万'
      return String(val)
    }

    // 格式化龙头猎手热度字段（个股热度/板块热度，逻辑同成交额）
    const formatHunterHot = (val: number | null | undefined) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      if (val >= 100000000) return (val / 100000000).toFixed(2) + '亿'
      if (val >= 10000) return (val / 10000).toFixed(1) + '万'
      return String(val)
    }

    // 龙头猎手入选类型颜色
    const getHunterSelectTypeColor = (selectType: number) => {
      if (selectType === 5) return 'rgb(255,0,0)'              // 龙头启动：红色
      if (selectType === 4 || selectType === 3) return 'rgb(225,60,255)'  // 龙头加强/后排超车：紫色
      if (selectType === 2 || selectType === 1) return 'rgb(255,125,0)'   // 二波右侧/二波左侧：橙色
      return ''
    }

    // 龙头猎手入选类型文本
    const getHunterSelectTypeText = (selectType: number) => {
      const map: Record<number, string> = { 5: '龙头启动', 4: '龙头加强', 3: '后排超车', 2: '二波右侧', 1: '二波左侧' }
      return map[selectType] || '--'
    }

    // 格式化龙头猎手日期
    const formatHunterDate = (val: number | null | undefined) => {
      if (!val) return '--'
      return moment(val).format('YYYY-MM-DD')
    }

    // 根据 courseNo 获取原始列配置
    const columns = computed(() => {
      // 盟主趋势近30天热门案例使用特定的列配置
      if (isMengzhuTrend.value && props.mengzhuMainTab === 'hotCases') {
        return getHotCasesColumns(props.courseNo)
      }
      // 龙头猎手往期入选使用专属列配置
      if (isDragonHunter.value && props.dragonHunterMainTab === 'beforeChose') {
        const config = getCourseConfig(props.courseNo)
        return config.beforeChoseColumns || getTableColumns(props.courseNo)
      }
      // 龙头猎手热门案例使用 hotCasesColumns
      if (isDragonHunter.value && props.dragonHunterMainTab === 'hotCase') {
        return getHotCasesColumns(props.courseNo)
      }
      return getTableColumns(props.courseNo)
    })

    // 表头设置面板状态
    const headerSettingsVisible = ref(false)

    // 自定义列顺序（可拖动列的 key 数组）
    const customColumnOrder = ref<string[]>([])

    // 获取当前列表类型
    const currentListType = computed((): ListType | string => {
      if (isMengzhuTrend.value && props.mengzhuMainTab === 'hotCases') {
        return 'hotCases'
      }
      if (isDragonHunter.value) {
        return props.dragonHunterMainTab
      }
      if (isDragonSignal.value) {
        return props.dragonSignalStrategyCode
      }
      return 'signalPool'
    })

    // 获取当前信号类型
    const currentSignalType = computed((): SignalType | string => {
      if (isDragonHunter.value) {
        return props.dragonHunterSubTab
      }
      return props.mengzhuSignalType as SignalType || 'latest'
    })

    // 获取存储键
    const storageKey = computed(() => {
      // 兼容所有模式：将 courseNo、一级菜单、二级菜单组合为唯一键
      const isNeedSub = currentListType.value === 'signalPool' || isDragonSignal.value || (isDragonHunter.value && currentListType.value === 'newChose')
      return generateStorageKey(
        props.courseNo,
        currentListType.value as any,
        isNeedSub ? (currentSignalType.value as any) : undefined
      )
    })

    // 获取列配置版本
    const columnConfigVersion = computed(() => {
      return generateColumnConfigVersion(columns.value)
    })

    // 可拖动列（排除锁定列）
    const draggableColumns = computed(() => {
      const draggable = filterDraggableColumns(columns.value)
      // 如果有自定义顺序，按自定义顺序排列
      if (customColumnOrder.value.length > 0) {
        const orderedColumns: TableColumn[] = []
        const columnMap = new Map(draggable.map(col => [col.key, col]))
        for (const key of customColumnOrder.value) {
          const col = columnMap.get(key)
          if (col) {
            orderedColumns.push(col)
            columnMap.delete(key)
          }
        }
        // 添加剩余的列
        for (const col of columnMap.values()) {
          orderedColumns.push(col)
        }
        return orderedColumns
      }
      return draggable
    })

    // 默认可拖动列（用于恢复默认）
    const defaultDraggableColumns = computed(() => {
      return filterDraggableColumns(columns.value)
    })

    // 最终显示的列（锁定列 + 自定义顺序的可拖动列）
    const displayColumns = computed(() => {
      if (customColumnOrder.value.length > 0) {
        return applyColumnOrder(columns.value, customColumnOrder.value)
      }
      return columns.value
    })

    // 加载保存的列顺序
    const loadColumnOrder = () => {
      const saved = getColumnOrder(storageKey.value)
      if (saved) {
        // 验证配置是否兼容
        if (validateColumnConfig(saved.columnKeys, columns.value) && saved.version === columnConfigVersion.value) {
          customColumnOrder.value = saved.columnKeys
        } else {
          // 配置不兼容，清除旧设置
          clearColumnOrder(storageKey.value)
          customColumnOrder.value = []
        }
      } else {
        customColumnOrder.value = []
      }
    }

    // 打开表头设置面板
    const openHeaderSettings = () => {
      headerSettingsVisible.value = true
    }

    // 处理表头设置应用
    const handleHeaderSettingsApply = (newColumns: TableColumn[]) => {
      const newOrder = newColumns.map(col => col.key)
      customColumnOrder.value = newOrder
      // 保存到 localStorage
      saveColumnOrder(storageKey.value, newOrder, columnConfigVersion.value)
    }

    // 处理表头设置取消
    const handleHeaderSettingsCancel = () => {
      // 不做任何操作，保持原有顺序
    }

    // 监听存储键变化，重新加载列顺序
    watch(storageKey, () => {
      loadColumnOrder()
    })

    // 数据源变化时重新观察行（用于 IntersectionObserver 订阅优化）
    watch(() => props.dataSource.length, () => {
      refreshObserver()
    })

    // 龙头信号从回测 tab 切回时，重新观察行恢复 WebSocket 订阅
    watch(() => props.mengzhuSignalType, (newType, oldType) => {
      if (oldType === 'backtest' && newType !== 'backtest') {
        // DOM 重建后重新观察，延迟要足够长确保 DOM 已渲染
        setTimeout(() => {
          createObserver()
          observeRows()
        }, 150)
      }
    })

    // 龙头猎手从 dataTrack 切回时，table-container DOM 重建，需要重新创建 IntersectionObserver
    watch(() => props.dragonHunterMainTab, (newTab, oldTab) => {
      if (oldTab === 'dataTrack' && newTab !== 'dataTrack') {
        setTimeout(() => {
          createObserver()
          observeRows()
          // 等 IntersectionObserver 回调稳定后 emit
          setTimeout(() => {
            debouncedEmitVisibleRange()
          }, 200)
        }, 150)
      }
    })

    // 组件挂载时加载列顺序
    onMounted(() => {
      loadColumnOrder()
    })
    const courseName = computed(() => getCourseName(props.courseNo))

    // 盟主趋势顶级 Tab（用于模板中访问）
    const currentMengzhuMainTab = computed(() => props.mengzhuMainTab)

    // 获取对齐类名
    const getAlignClass = (align?: string) => {
      switch (align) {
        case 'left': return 'th-left'
        case 'center': return 'th-center'
        case 'right': return 'th-right'
        default: return 'th-left'
      }
    }

    // 获取固定列的 left 值
    const getFixedLeft = (currentIndex: number) => {
      let left = 0
      for (let i = 0; i < currentIndex; i++) {
        const col = displayColumns.value[i]
        if (col.fixed === 'left') {
          left += typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10) || 0
        }
      }
      return left
    }

    // 格式化单元格值
    const formatCellValue = (val: any, formatter?: string, symbol?: string) => {
      switch (formatter) {
        case 'number': return formatPrice(val, symbol)
        case 'percent': return formatPercent(val)
        case 'amount': return formatAmount(val)
        default: return val ?? '--'
      }
    }

    // 格式化最新价：如果为0则显示--
    const formatLastPx = (lastPx: number | null | undefined, symbol?: string) => {
      if (lastPx === null || lastPx === undefined || lastPx === 0) {
        return '--'
      }
      return formatPrice(lastPx, symbol)
    }

    // 格式化涨幅：如果最新价为0则显示--
    const formatPxChangRate = (lastPx: number | null | undefined, pxChangRate: number | null | undefined) => {
      if (lastPx === null || lastPx === undefined || lastPx === 0) {
        return '--'
      }
      return formatPercent(pxChangRate)
    }

    // 格式化入选日涨跌幅：四舍五入取小数点后两位，为正不显示+号，为负显示-号
    const formatSelectRate = (val: number | null | undefined) => {
      if (val === null || val === undefined) {
        return '--'
      }
      return (val * 100).toFixed(2) + '%'
    }

    // 获取单元格样式类
    const getCellClass = () => {
      // 涨跌相关列不再使用CSS类，改用内联样式处理颜色
      return []
    }

    // 获取单元格样式
    const getCellStyle = (col: TableColumn, colIndex: number, record: PoolData) => {
      const style: Record<string, string> = {}
      if (col.fixed === 'left') {
        style.left = getFixedLeft(colIndex) + 'px'
      }
      // 固定颜色（从配置中读取，如金额列 #19B7E1，总量/换手列 #959EAD）
      if (col.color) {
        style.color = col.color
      }
      // 特殊列动态颜色
      if (col.key === 'macd') {
        style.color = getMacdColor(record.macd)
      }
      // 四维猎手的资金标尺使用动态颜色，盟主趋势/龙头信号/龙头猎手的成交额使用配置中的固定颜色
      if (col.key === 'amount' && !isMengzhuTrend.value && !isDragonSignal.value && !isDragonHunter.value) {
        style.color = getAmountColor(record.amount)
      }
      // 最高涨幅（四维猎手）：>0 红色，<0 绿色，=0 灰色
      if (col.key === 'highRate') {
        const val = record.highRate
        if (val === null || val === undefined || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 最大涨幅（箱体擒龙）：>0 红色，<0 绿色，=0 灰色（与涨幅逻辑一致）
      if (col.key === 'highPct') {
        const val = record.highPct
        if (val === null || val === undefined || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 最新价、涨幅、入选价：根据涨幅值显示颜色
      // >0: 红色; <0: 绿色; =0: 灰色
      // 盟主趋势的入选价使用代码列颜色，不使用涨跌色
      // 箱体擒龙的入选价(selectedPrice)使用名称列颜色，和四维猎手的名称列保持一致
      if (['lastPx', 'pxChangRate', 'selectedPx', 'closePx', 'selectedPrice', 'selectRate'].includes(col.key)) {
        // 四维猎手的入选日涨跌幅(selectRate)使用自身的涨跌值判断颜色，而不是当前涨幅
        if (col.key === 'selectRate') {
          const rate = record.selectRate
          if (rate === null || rate === undefined || rate === 0) {
            style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
          } else if (rate > 0) {
            style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
          } else {
            style.color = props.theme === 'dark' ? '#39e365' : '#439731'
          }
        } else if (isBoxDragon.value && (col.key === 'closePx' || col.key === 'selectedPrice')) {
          // 箱体擒龙的入选价使用名称列颜色（和四维猎手的名称列一致）
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#333'
        } else if (isDragonSignal.value && col.key === 'selectedPrice') {
          // 龙头信号的入选价使用代码列颜色（和四维猎手的代码列一致）
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
        } else {
          // 盟主趋势的入选价(buyPx)不在这里处理，使用代码列颜色
          const rate = record.pxChangRate
          if (rate === null || rate === undefined || rate === 0) {
            style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
          } else if (rate > 0) {
            style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
          } else {
            // rate < 0
            style.color = props.theme === 'dark' ? '#39e365' : '#439731'
          }
        }
      }
      // 盟主趋势最高涨幅：根据maxRate自身的值显示颜色（和四维猎手涨幅一样）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'maxRate') {
        const rate = record.maxRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势累计涨幅：根据cumRate自身的值显示颜色（红涨绿跌黑平，和四维猎手最新价一致）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'cumRate') {
        const rate = record.cumRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势资金：根据fund自身的值显示颜色（红正绿负黑平）
      if (isMengzhuTrend.value && col.key === 'fund') {
        const val = parseFloat(record.fund)
        if (isNaN(val) || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势特定列使用代码列颜色（和四维猎手的代码列一致）
      // 信号池：入选价、题材板块、离场信号时间
      // 近30天热门案例：入选价、入选日期、离场日期
      if (isMengzhuTrend.value && ['buyPx', 'plateName', 'sellTime', 'buyTime'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 箱体擒龙总量和换手列使用代码列颜色（和四维猎手的代码列一致）
      if (isBoxDragon.value && ['volume', 'turnoverRatio'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 龙头猎手：入选价、个股热度、所属板块、板块热度、入选时间 使用代码列颜色
      if (isDragonHunter.value && ['price', 'symbolHot', 'plateName', 'plateHot', 'hunterDateTime'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 龙头信号：个股热度、所属板块 使用代码列颜色
      if (isDragonSignal.value && ['stockHot', 'dragonPlateName'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      return style
    }

    // 排序处理：首次点击降序↓ → 再次点击升序↑ → 再次点击取消 → 循环
    const handleSort = (field: string) => {
      if (sortField.value === field) {
        if (sortOrder.value === 'descend') {
          sortOrder.value = 'ascend'
        } else if (sortOrder.value === 'ascend') {
          sortField.value = ''
          sortOrder.value = ''
        } else {
          sortOrder.value = 'descend'
        }
      } else {
        sortField.value = field
        sortOrder.value = 'descend'
      }
      emit('change', {
        pagination: null,
        sorter: sortField.value ? { field: sortField.value, order: sortOrder.value } : {}
      })
    }

    // 滚动加载处理
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target) return

      // 同步表头水平滚动
      if (tableHeaderRef.value) {
        tableHeaderRef.value.scrollLeft = target.scrollLeft
      }

      const { scrollTop, scrollHeight, clientHeight } = target
      if (scrollHeight - scrollTop - clientHeight < 50) {
        if (props.hasMore && !props.loadingMore && !props.loading) {
          emit('load-more')
        }
      }
    }

    // 用于区分单击和双击的定时器
    let clickTimer: ReturnType<typeof setTimeout> | null = null
    const CLICK_DELAY = 250 // 单击延迟时间，用于区分双击

    onMounted(() => {
      // 创建 IntersectionObserver 并开始观察
      createObserver()
      observeRows()
    })
    onUnmounted(() => {
      // 清理定时器
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }
      if (visibleChangeTimer) {
        clearTimeout(visibleChangeTimer)
        visibleChangeTimer = null
      }
      // 断开 IntersectionObserver
      if (intersectionObserver) {
        intersectionObserver.disconnect()
        intersectionObserver = null
      }
    })

    // 单击行
    const handleRowClick = (record: PoolData) => {
      // isClick=1 弹窗模式：没有单击，直接返回
      if (props.isClick === 1) return

      // 清除之前的定时器（如果有）
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      // isClick=2 内嵌模式：延迟执行单击，等待判断是否为双击
      clickTimer = setTimeout(() => {
        const code = record.symbol
        const market = record.market || 'sz'
        const url = `gw://funccall?funcname=OnFireStyleEvent;Command=set_focus_stock;param=<params><code>${code}</code><market_letter>${market}</market_letter></params>`
        window.open(url)
        console.log('danji ', url)
        emit('row-click', record)
        clickTimer = null
      }, CLICK_DELAY)
    }

    // 双击行
    const handleRowDblClick = (record: PoolData) => {
      // 取消单击的延迟执行
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      setTimeout(() => {
        console.log('shuangji')
        const code = record.symbol
        const market = record.market || 'sz'
        const clickedIndex = props.dataSource.findIndex((item: PoolData) => item.symbol === record.symbol)
        const afterClicked = props.dataSource.slice(clickedIndex + 1) as PoolData[]
        const beforeClicked = props.dataSource.slice(0, clickedIndex) as PoolData[]
        const reorderedList: PoolData[] = [record].concat(afterClicked).concat(beforeClicked)
        const stockList = reorderedList.map((item: PoolData) => `${item.market || 'sz'}:${item.symbol}`).join(',')

        // 第一个调用：设置股票列表
        const url1 = `gw://funccall?funcname=SetCurStockList;list=${stockList}`
        console.log('shuangji url1:', url1)
        window.open(url1)
        // 第二个调用：打开K线图，加延时
        setTimeout(() => {
          const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style></params>`
          console.log('shuangji url2:', url2)
          window.open(url2)
          // 第三个调用：显示指标，再加延时
          setTimeout(() => {
            const indAlias = (props.productData && props.productData[0]?.poolName) || courseName.value
            const url3 = `gw://funccall?funcname=OnFireCommand;Command=yin_ind_show;param=<params><ind_alias>${indAlias}</ind_alias><ingnore_paras>${1}</ingnore_paras></params>`
            console.log('shuangji url3:', url3)
            window.open(url3)
            emit('row-dblclick', record)
          }, 100)
        }, 100)
      }, 50)
    }


    // 趋势势能相关
    // 大于0：强势 黄色；小于0：弱势 绿色；等于0：-
    const getMacdColor = (val: number) => {
      if (val > 0) return '#f8a000'
      if (val < 0) return props.theme === 'dark' ? '#39e365' : '#439731'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getMacdText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${val.toFixed(2)})`
    }

    // 资金标尺相关
    // 大于0：强势 红色；小于0：弱势 蓝色；等于0：-
    const getAmountColor = (val: number) => {
      if (val > 0) return props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
      if (val < 0) return props.theme === 'dark' ? '#00ffff' : '#0079d5'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const formattedVal = formatAmount(val)
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${formattedVal})`
    }

    // 盟主趋势成交额格式化（只显示金额，和四维猎手的成交额列一致）
    const getMengzhuAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      // 只显示金额，不显示强势/弱势
      return formatAmount(val)
    }

    // 题材追踪相关
    // 核心：紫色；普通：黄色；未入选：黑色
    const getPlateStyle = (type: string | number) => {
      const typeStr = String(type)
      if (typeStr === '1') return { color: '#883cd1' } // 核心 紫色
      if (typeStr === '2') return { color: '#f8a000' } // 普通 黄色
      return { color: props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d' } // 未入选 黑色
    }
    const getPlateText = (type: string | number, name: string) => {
      const typeStr = String(type)
      const typeStrname = String(name)
      if (typeStr === '1') return `${name}(核心)`
      if (typeStr === '2') return `${name}(普通)`
      if (typeStrname !== '' && typeStr === '0') return `${name}(未入选)`
      return `——`
    }

    // 筹码大师相关
    // 筹码状态颜色
    const getChipStateColorStyle = (state: string, stateColor?: number) => {
      return getChipStateColor(state, stateColor, props.theme as 'light' | 'dark')
    }

    // 筹码形态颜色（固定灰色）
    const getChipShapeColorStyle = () => {
      return getChipShapeColor(props.theme as 'light' | 'dark')
    }

    // 入选时间颜色（与序号相同）
    const getInPoolTimeColorStyle = () => {
      return getInPoolTimeColor(props.theme as 'light' | 'dark')
    }

    // 格式化入选时间
    const formatInPoolTimeValue = (timestamp: number | undefined | null) => {
      return formatInPoolTime(timestamp)
    }

    // 盟主趋势相关
    // 盟主资金格式化（亿）
    const formatMengzhuFund = (val: string | number | undefined | null) => {
      if (val === null || val === undefined || val === '') return '--'
      const num = typeof val === 'string' ? parseFloat(val) : val
      if (isNaN(num)) return '--'

      const str = num.toFixed(2)

      // 等于 0 → 只返回 0.00
      if (num == 0) {
        return '0.00'
      }

      // 大于 0 → 带 亿
      return str + '亿'
    }

    // 格式化盟主趋势日期
    const formatMengzhuDateValue = (timestamp: number | undefined | null) => {
      return formatMengzhuDate(timestamp)
    }

    // 判断盟主趋势最新信号模式下，0值是否应该显示为无效（"-"）
    // 逻辑：对比最后一个有效交易日和信号池的 updateTime 日期
    // 如果日期相同：后端返回的涨跌幅字段为0时，显示为"-"（无效）
    // 如果交易日大于信号池日期（+1天）：涨跌幅字段正常显示，0是有效值，显示为"0%"
    // 历史信号模式：0值始终显示为 "0.00%"（有效值）
    const shouldShowZeroAsInvalid = () => {
      // 只在盟主趋势最新信号模式下生效
      if (!isMengzhuTrend.value) return true
      if (props.mengzhuMainTab !== 'signalPool') return true
      // 历史信号模式：0值是有效值，不显示为无效
      if (props.mengzhuSignalType !== 'latest') return false

      const lastTradingDay = props.mengzhuLastTradingDay
      const updateTimeStr = props.mengzhuUpdateTime

      if (!lastTradingDay || !updateTimeStr) return true

      // 获取 updateTime 的日期部分（YYYY-MM-DD）
      const updateTimeDate = moment(updateTimeStr).format('YYYY-MM-DD')

      // 如果最后一个有效交易日和信号池日期相同，0值显示为无效
      // 如果最后一个有效交易日大于信号池日期，0值是有效值
      return lastTradingDay <= updateTimeDate
    }

    // 格式化盟主趋势最高涨幅
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuMaxRate = (val: number | undefined | null) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // maxRate 是小数形式（如 0.05 表示 5%），需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 格式化盟主趋势累计涨幅
    // 信号池：需要乘以100（cumRate 是小数形式，如 0.1694 表示 16.94%）
    // 近30天热门案例：不需要乘以100（cumRate 已经是百分比形式，如 16.94 表示 16.94%）
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuCumRate = (val: number | undefined | null, mainTab?: string) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 近30天热门案例：0值始终显示为"-"
        if (mainTab === 'hotCases') {
          return '-'
        }
        // 信号池：判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // 近30天热门案例不需要乘以100
      if (mainTab === 'hotCases') {
        return val.toFixed(2) + '%'
      }
      // 信号池需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 盟主趋势涨跌幅颜色
    const getMengzhuChangeColor = (value: number | undefined | null) => {
      return getChangeColor(value, props.theme as 'light' | 'dark')
    }

    const openAiInterpDetail = (record: PoolData) => {
      emit('open-ai-interp', record)
    }

    const getShortReason = (reason: string) => {
      if (!reason) return ''
      return reason.length > 6 ? reason.slice(0, 6) + '...' : reason
    }

    return {
      tableWrapperRef,
      tableBodyRef,
      tableHeaderRef,
      sortField,
      sortOrder,
      columns,
      displayColumns,
      draggableColumns,
      defaultDraggableColumns,
      headerSettingsVisible,
      courseName,
      shouldMask,
      isMengzhuTrend,
      isBoxDragon,
      isDragonSignal,
      currentMengzhuMainTab,
      getDisplayName,
      getDisplayCode,
      getAlignClass,
      getFixedLeft,
      formatCellValue,
      formatLastPx,
      formatPxChangRate,
      formatSelectRate,
      getCellClass,
      getCellStyle,
      handleSort,
      handleScroll,
      handleRowClick,
      handleRowDblClick,
      openHeaderSettings,
      handleHeaderSettingsApply,
      handleHeaderSettingsCancel,
      getMacdColor,
      getMacdText,
      getAmountColor,
      getAmountText,
      getMengzhuAmountText,
      getPlateStyle,
      getPlateText,
      // 筹码大师相关
      getChipStateColorStyle,
      getChipShapeColorStyle,
      getInPoolTimeColorStyle,
      formatInPoolTimeValue,
      // 盟主趋势相关
      formatMengzhuFund,
      formatMengzhuDateValue,
      formatMengzhuMaxRate,
      formatMengzhuCumRate,
      getMengzhuChangeColor,
      openAiInterpDetail,
      getShortReason,
      tableContainerStyle,
      settingsIconHover,
      settingsIconSrc,
      // 龙头信号相关
      formatStockHot,
      // 龙头猎手相关
      isDragonHunter,
      formatHunterHot,
      getHunterSelectTypeColor,
      getHunterSelectTypeText,
      formatHunterDate
    }
  }
})
